import "./KiboxList.scss";
import { useState, useEffect, useCallback, useMemo} from "react";
import { notification, Button, Space, Tag } from "antd";
import { FormOutlined, RightOutlined, DownOutlined } from "@ant-design/icons";
import { hasPrivilege } from "../../api/token.js";
import service from "../../services/Service.js";
import { useGetModelsLookupQuery, useGetVersionsLookupQuery, useGetTenantsLookupQuery, useGetRoutersLookupQuery } from '../../services/lookupApi.js';
import dayjs from 'dayjs';
import FilterColumns from "../FilterColumns/index.js";
import { withLocale } from "../../l10n/index.jsx";
import DataTable from "../DataTable/index.js";
import DeleteButton from "../DeleteButton/DeleteButton.jsx";
import withNavigation from '../hooks/withNavigation.jsx';
import Excelexport from '../../utils/excelexport.jsx';
import Loading from "../../utils/loading.jsx";
import { formatDate, compareDates } from '../../utils/dates';
import { GetColumnLiteralSearchProps, getSubscriptionStateLabel, getStatusLabel, getRouterLabel } from '../../utils/functions.jsx';
import { buildKiboxExportRows, compareBySpareDate } from '../../utils/kiboxExport.jsx';
import { STATE_ACTIVE, STATE_INACTIVE, STATE_CANCELLED, STATE_NOT_APPLICABLE } from '../../utils/constants.js';

const EMPTY_ARRAY = [];

function KiboxList({ lang, navigate }) {

  const [loading, setLoading] = useState(true);
  const [rawRows, setRawRows] = useState([]);
  const [botonDisabled, setBotonDisabled] = useState(true);
  const [selectedId, setSelectedId] = useState("");
  const [selectedRow, setSelectedRow] = useState(null);
  const [userSelectedColumns, setUserSelectedColumns] = useState(null);
  const [filteredRows, setFilteredRows] = useState([]);


  const { data: models = EMPTY_ARRAY } = useGetModelsLookupQuery();
  const { data: versions = EMPTY_ARRAY } = useGetVersionsLookupQuery();
  const { data: tenants = EMPTY_ARRAY } = useGetTenantsLookupQuery();
  const { data: routers = EMPTY_ARRAY } = useGetRoutersLookupQuery();

  const kiboxModels = useMemo(
    () => models.filter(model => ['kibox', 'kibox_legacy'].includes(model.device_type)),
    [models]
  );

  const modelFilter = useMemo(
    () => kiboxModels.map(model => ({ text: model.name, value: model.uuid })),
    [kiboxModels]
  );

  const versionFilter = useMemo(
    () => kiboxModels
      .map(model => ({
        text: model.name,
        value: model.uuid,
        children: versions
          .filter(v => v.model_uuid === model.uuid)
          .map(v => ({ text: v.name, value: v.uuid }))
      }))
      .filter(group => group.children.length > 0),
    [kiboxModels, versions]
  );

  const tenantsFilter = useMemo(() => {
    const tenantFilter = tenants.map(tenant => ({ text: tenant.name, value: tenant.uuid }));
    tenantFilter.push({ text: lang.handle("Sin organización"), value: null });
    return tenantFilter;
  }, [tenants, lang]);

  const routerById = useMemo(
    () => new Map(routers.map(router => [router.uuid, router])),
    [routers]
  );

  const getListData = useCallback(async () => {
    try {
      const res = await service.get('boards');

      if (res.data.ok === false) {
        notification["error"]({ message: lang.handle(res.data.message )});
        setRawRows([]);
        return;
      }

      setRawRows(res.data);
    } catch (err) {
      console.log("Error devuelto: " + err);
    }
  }, [lang]);

  const buildTreeData = (rows) => {
    const parentByUuid = new Map();
    const roots = [];

    //Indexamos a los padres
    rows.forEach((row) => {
      if (row.original_uuid == null) {
        parentByUuid.set(row.uuid, row );
      }
    });

    // Construimos la estructura de árbol
    rows.forEach((row) => {
      // Los padres se añaden directamente
      if (row.original_uuid == null) {
        roots.push(row);
      //Los hijos buscan a su padre en el mapa y se añaden como hijos al padre,
      // si no encuentran a su padre se añaden como huerfanos al listado.
      } else {
        const parent = parentByUuid.get(row.uuid);
        if (parent) {
          if (!parent.children) parent.children = [];
          parent.children.push(row);
        } else {
          roots.push(row);
        }
      }
    })

    // Ordenar los repuestos de cada equipo por fecha de asignación (spare_date). 
    roots.forEach((parent) => {
      if (parent.children && parent.children.length > 1) {
        parent.children.sort(compareBySpareDate);
      }
    });

    return roots;
  };

  const enrichedData = useMemo(() => rawRows.map((element) => ({
    ...element,
    device_name: element.device_name || lang.handle("Sin instalación"),
    version_name: element.version_name || lang.handle("Sin versión"),
    model_name: element.model_name || lang.handle("Sin modelo"),
    tenant_name: element.tenant_name || lang.handle("Sin organización"),
    router_name: element.router_uuid
      ? (getRouterLabel(routerById.get(element.router_uuid)) || lang.handle('Router no disponible'))
      : (element.ki_router_sn || ''),
  })), [rawRows, routerById, lang]);

  // Los repuestos cuelgan de su placa padre
  const treeData = useMemo(() => buildTreeData(enrichedData), [enrichedData]);

  useEffect(() => {
    const savedColumns = localStorage.getItem('kiboxColumns_visible');
    if (savedColumns) {
      try {
        const parsed = JSON.parse(savedColumns);
        if (Array.isArray(parsed) && parsed.length > 0) {
          const migrated = parsed.map(key => key === 'ki_router_sn' ? 'router_name' : key);
          setUserSelectedColumns(migrated);
        }
      } catch {
        localStorage.removeItem('kiboxColumns_visible');
      }
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      try {
        await getListData();
      } catch (err) {
        console.log("Error en loadData: " + err);
      } finally {
        setLoading(false);
      }
    };

    loadData();

  }, [getListData]);

  const allColumns = useMemo(() => [
    {
      title: 'Id',
      dataIndex: "ki_id",
      ...GetColumnLiteralSearchProps("ki_id", lang),
    },
    {
      title: lang.handle('Pedido'),
      dataIndex: "order_id",
      ...GetColumnLiteralSearchProps("order_id", lang),
    },
    {
      title: lang.handle('Instalación'),
      dataIndex: "device_name",
      ...GetColumnLiteralSearchProps("device_name", lang),
    },
    {
      title: lang.handle('Organización'),
      dataIndex: "tenant_name",
      filters: tenantsFilter,
      onFilter: (value, record) => record.tenant_uuid === value,
    },
    {
      title: lang.handle('Cliente final'),
      dataIndex: "final_client",
      ...GetColumnLiteralSearchProps("final_client", lang),
    },
    {
      title: lang.handle('UUID'),
      dataIndex: "uuid",
      ...GetColumnLiteralSearchProps("uuid", lang),
    },
    {
      title: lang.handle('UUID Original'),
      dataIndex: "original_uuid",
      ...GetColumnLiteralSearchProps("original_uuid", lang),
    },
    {
      title: lang.handle('Número de serie'),
      dataIndex: "serial_number",
      ...GetColumnLiteralSearchProps("serial_number", lang),
    },
    {
      title: lang.handle('Orden de trabajo'),
      dataIndex: "work_order",
      ...GetColumnLiteralSearchProps("work_order", lang),
    },
    {
      title: lang.handle('Nombre comercial'),
      dataIndex: ["comercial_info", "name"],
      ...GetColumnLiteralSearchProps(["comercial_info", "name"], lang),
      onCell: () => ({
        className: 'comercial-info-cell'
      }),
      onHeaderCell: () => ({
        className: 'comercial-info-header'
      })
    },
    {
      title: lang.handle('Pedido comercial'),
      dataIndex: ["comercial_info", "order_id"],
      ...GetColumnLiteralSearchProps(["comercial_info", "order_id"], lang),
      onCell: () => ({
        className: 'comercial-info-cell'
      }),
      onHeaderCell: () => ({
        className: 'comercial-info-header'
      })
    },
    {
      title: lang.handle('Número de serie comercial'),
      dataIndex: ["comercial_info", "serial_number"],
      ...GetColumnLiteralSearchProps(["comercial_info", "serial_number"], lang),
      onCell: () => ({
        className: 'comercial-info-cell'
      }),
      onHeaderCell: () => ({
        className: 'comercial-info-header'
      })
    },
    {
      title: lang.handle('Modelo del equipo'),
      dataIndex: ["comercial_info", "model"],
      ...GetColumnLiteralSearchProps(["comercial_info", "model"], lang),
      onCell: () => ({
        className: 'comercial-info-cell'
      }),
      onHeaderCell: () => ({
        className: 'comercial-info-header'
      })
    },
    {
      title: lang.handle('Estado Comercial'),
      dataIndex: ["comercial_info", "state"],
      filters: [
        { text: lang.handle("Stock"), value: "Stock" },
        { text: lang.handle('Asignado'), value: 'Asignado' },
        { text: lang.handle('Terminado'), value: 'Terminado' },
      ],
      onCell: () => ({
        className: 'comercial-info-cell'
      }),
      onHeaderCell: () => ({
        className: 'comercial-info-header'
      }),
      onFilter: (value, record) =>
        record.comercial_info && record.comercial_info.state && record.comercial_info.state.toLowerCase() === value.toLowerCase(),
    },
    {
      title: lang.handle('kiconex plan'),
      dataIndex: "ki_subscription_name",
      ...GetColumnLiteralSearchProps("ki_subscription_name", lang),
    },
    {
      title: lang.handle('Fecha fabricación'),
      dataIndex: "date",
      sorter: (a, b) => compareDates(a.date, b.date),
      sortDirections: ["descend", "ascend"],
      render: (data) => <p> {formatDate(data)} </p>,
    },
    {
      title: lang.handle('Fecha Conexión'),
      dataIndex: "ki_connection_date",
      sorter: (a, b) => compareDates(a.ki_connection_date, b.ki_connection_date),
      sortDirections: ["descend", "ascend"],
      render: (data) => <p> {formatDate(data)} </p>,
    },
    {
      title: lang.handle('Version hardware'),
      dataIndex: "hardware_version",
    },
    {
      title: "MAC / IMEI",
      dataIndex: "physical_id",
      ...GetColumnLiteralSearchProps("physical_id", lang),
    },
    {
      title: lang.handle('Modelo'),
      dataIndex: "model_name",
      filters: modelFilter,
      onFilter: (value, record) => record.model_uuid === value,
      sorter: (a, b) => (a.model_name || '').localeCompare(b.model_name || ''),
    },
    {
      title: lang.handle('Versión'),
      dataIndex: "version_name",
      filters: versionFilter,
      onFilter: (value, record) => record.version_uuid === value,
      sorter: (a, b) => (a.version_name || '').localeCompare(b.version_name || ''),
    },
    {
      title: "ICC",
      dataIndex: "icc",
      ...GetColumnLiteralSearchProps("icc", lang),
    },
    {
      title: lang.handle('Router kiconex'),
      dataIndex: "router_name",
      ...GetColumnLiteralSearchProps("router_name", lang),
    },
    {
      title: lang.handle('Fecha de expedición'),
      dataIndex: "expedition_date",
      sorter: (a, b) => compareDates(a.expedition_date, b.expedition_date),
      sortDirections: ["descend", "ascend"],
      render: (data) => formatDate(data),
    },
    {
      title: lang.handle('Notas'),
      dataIndex: "notes",
      ...GetColumnLiteralSearchProps("notes", lang),
    },
    {
      title: lang.handle('Estado'),
      dataIndex: "state",
      filters: [
        { text: lang.handle('Fabricado'), value: 'Fabricado' },
        { text: lang.handle('Asignado'), value: 'Asignado' },
        { text: lang.handle('Terminado'), value: 'Terminado' },
        { text: lang.handle('Eliminado'), value: 'Eliminado' },
      ],
      onFilter: (value, record) =>
        record.state && record.state.toLowerCase() === value.toLowerCase(),
      render: (text) => getStatusLabel(text, lang),
    },
    {
      title: lang.handle('VPN'),
      dataIndex: "vpn",
      filters: [
        { text: lang.handle('Activada'), value: 1 },
        { text: lang.handle('Desactivada'), value: 0 },
      ],
      onFilter: (value, record) => record.vpn === value,
      render: (data) =>
        data === 1
          ? <Tag color="green"> {lang.handle('Activada')} </Tag>
          : <Tag color="red"> {lang.handle('Desactivada')} </Tag>,
    },
    {
      title: lang.handle('Estado de subscripción'),
      dataIndex: "ki_subscription_state",
      filters: [
        { text: lang.handle('Activo'), value: STATE_ACTIVE },
        { text: lang.handle('Lista para activación'), value: STATE_INACTIVE },
        { text: lang.handle('Cancelado'), value: STATE_CANCELLED },
        { text: lang.handle('No procede'), value: STATE_NOT_APPLICABLE },
      ],
      onFilter: (value, record) => record.ki_subscription_state === value,
      render: (text) => getSubscriptionStateLabel(text, lang),
      }
  ], [lang, modelFilter, versionFilter, tenantsFilter]);

  const displayedColumns = useMemo(() => {
    if (userSelectedColumns === null || userSelectedColumns.length === 0) return allColumns;
    return allColumns.filter(col => {
      const key = Array.isArray(col.dataIndex) ? col.dataIndex.join('.') : col.dataIndex;
      return userSelectedColumns.includes(key);
    });
  }, [userSelectedColumns, allColumns]);

  const handleTableChange = useCallback((pagination, filters, sorter, extra) => {
    setFilteredRows(extra.currentDataSource);
  }, []);

  const rowSelection = {
    onChange: (selectedRowKeys, selectedRows) => {
      setBotonDisabled(false);
      setSelectedId(selectedRows[0].ki_id);
      setSelectedRow(selectedRows[0]);
    }
  };

  const changeColumns = useCallback((newColumns) => {
    if (newColumns.length === 0) {
      setUserSelectedColumns(null);
      localStorage.removeItem('kiboxColumns_visible');
    } else {
      // Guardar solo las keys (dataIndex) para evitar perder las funciones al serializar
      const keys = newColumns.map(c => Array.isArray(c.dataIndex) ? c.dataIndex.join('.') : c.dataIndex);
      setUserSelectedColumns(keys);
      localStorage.setItem('kiboxColumns_visible', JSON.stringify(keys));
    }
  }, []);

  const getExportData = () =>
    buildKiboxExportRows(filteredRows.length > 0 ? filteredRows : treeData, displayedColumns, lang);

  const deletePlaca = async () => {
    try {
      setLoading(true);
      const res = await service.del("boards/" + selectedId, selectedId);
      if (res.data.ok === false) {
        await getListData();
        notification["error"]({
          message: lang.handle(res.data.message),
        });
      } else {
        setBotonDisabled(true);
        notification["success"]({message: lang.handle('Placa borrada correctamente')});
        await getListData()
      }
    } catch (err) {
      console.log("Error devuelto: " + err);
      await getListData()
    } finally {
      setLoading(false);
    }
  };

  const updatePlaca = () => {
    navigate(`/boards/edit/1/${selectedId}`);
  };

  const deleteInfo = useMemo(() => {
    if (!selectedRow) return { willBeDeleted: [], willStay: [] };

    const isSpare = !!selectedRow.original_uuid;
    const hasModem = !!selectedRow.modem_imei;
    const hasRouter = !!selectedRow.router_uuid;

    const willBeDeleted = isSpare
      ? [lang.handle('La información del repuesto')]
      : [
          lang.handle('La información del box'),
          lang.handle('La suscripción del plan'),
          ...(!hasRouter ? [lang.handle('La suscripción M2M relacionada')] : []),
          lang.handle('La información de la VPN (si existe)'),
        ];

    const willStay = [
      ...(isSpare ? [lang.handle('Las suscripciones y la VPN del box original')] : []),
      ...(hasModem ? [lang.handle('El modem Teltonika volverá a estar disponible en stock')] : []),
      ...(hasRouter ? [lang.handle('El router asignado, con su suscripción M2M y su VPN')] : []),
    ];

    return { willBeDeleted, willStay };
  }, [selectedRow, lang]);

  let selection = null;
  if (hasPrivilege('administration_delete') || hasPrivilege('administration_edit') || hasPrivilege('administration_edit_comercial')) {
    selection =
    {
      type: 'radio',
      ...rowSelection,
    };
  }
  
  return (
    <div>
      {loading && <Loading loading={loading} />}
      <div className="row">
        <div className="col">
          <FilterColumns 
            columns={allColumns} 
            selectedColumns={userSelectedColumns === null ? [] : displayedColumns}  
            changeColumns={changeColumns} 
          />
        </div>
      </div>
      <DataTable
        rowSelection={selection}
        columns={displayedColumns}
        rowKey="ki_id"
        dataSource={treeData}
        onChange={handleTableChange}
        loading={false}
        scroll={displayedColumns.length > 5 ? { x: 'max-content' } : null}
        rowClassName={(record) => {
          let classNames = '';
          
          if (record.ki_subscription_state === STATE_CANCELLED) {
            classNames += 'row-cancelled';
          }
          if (record.original_uuid !== null) {
            classNames += ' row-hijo';
          } else {
            classNames += ' row-padre';
          }
          return classNames;
        }}
        expandable={{
          expandIconColumnIndex: 0,
          expandIcon: ({ expanded, onExpand, record }) => (
            record.children && record.children.length > 0 ? (
              <span
                onClick={(e) => onExpand(record, e)}
                className="expand-icon"
              >
                {expanded ? <DownOutlined /> : <RightOutlined />}
              </span>
            ) : null
          ),
          expandedRowRender: () => null,
        }}
      />
      <div className="button-panel">
        <Space>

          <Excelexport
            fileName={'kibox_list_' + dayjs().format('YYYYMMDD_HHmmss')}
            fileData={() => [{ name: 'kibox', data: getExportData() }]}
            disabled={(filteredRows.length || treeData.length) === 0}
            label={lang.handle('Exportar a Excel')}
          />

          {hasPrivilege('administration_delete') &&
            <DeleteButton
              onEvent={deletePlaca}
              buttonDisabled={botonDisabled}
              willBeDeleted={deleteInfo.willBeDeleted}
              willStay={deleteInfo.willStay}
            />
          }

          {(hasPrivilege('administration_edit') || hasPrivilege('administration_edit_comercial')) && <Button
            type="primary"
            icon={<FormOutlined />}
            disabled={botonDisabled}
            onClick={updatePlaca}
          >
            {lang.handle('Editar')}
          </Button>}
        </Space>
      </div>
    </div>
  );
}

export default withLocale(withNavigation(KiboxList));
